'use client'

import React, { useState, useEffect } from 'react';
import {
    Box,
    Flex,
    Text,
    Heading,
    Button,
    useColorModeValue,
    useToast,
    SimpleGrid,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td
} from '@chakra-ui/react';
import axiosInstance from '../utils/axiosInstance';

const AffiliateDashboard = () => {
    const toast = useToast();
    const [referralLink, setReferralLink] = useState('');
    const [summary, setSummary] = useState(null);
    const [payouts, setPayouts] = useState([]);

    // Fetch data
    useEffect(() => {
        const fetchData = async () => {
            try {
                const backendUrl = process.env.NODE_ENV === 'production'
                    ? process.env.REACT_APP_BACKEND_URL
                    : 'http://localhost:8080';
                const [summaryRes, referralRes, payoutsRes] = await Promise.all([
                    axiosInstance.get(`${backendUrl}/affiliate/summary`),
                    axiosInstance.get(`${backendUrl}/affiliate/referral`),
                    axiosInstance.get(`${backendUrl}/affiliate/payouts`)
                ]);

                setSummary(summaryRes.data);
                setReferralLink(referralRes.data.referral_link);
                setPayouts(payoutsRes.data.payouts);
            } catch (err) {
                toast({
                    title: 'Error loading affiliate data',
                    status: 'error',
                    duration: 4000,
                    isClosable: true,
                });
                console.error(err);
            }
        };

        fetchData();
    }, [toast]);

    const handleCopy = () => {
        navigator.clipboard.writeText(referralLink);
        toast({
            title: 'Referral link copied!',
            status: 'success',
            duration: 3000,
            isClosable: true
        });
    };

    const bgColor = useColorModeValue('gray.100', 'gray.700')


    return (
        <Box p={4} maxW="960px" mx="auto">
            {/* Header */}
            <Flex justify="space-between" align="center" mb={6}>
                <Flex align="center">
                    <Heading size="lg">Affiliate Dashboard</Heading>
                    {/*<Avatar src="https://i.pravatar.cc/100" mr={3} />*/}

                </Flex>
            </Flex>

            {/* Summary Cards */}
            {summary && (
                <SimpleGrid columns={[2, null, 5]} spacing={4} mb={6}>
                    <Box p={4} bg={bgColor} borderRadius="md">
                        <Text fontSize="sm" color="gray.500">Total Earnings</Text>
                        <Heading size="md">${(summary.total / 100).toFixed(2)}</Heading>
                    </Box>
                    <Box p={4} bg={bgColor} borderRadius="md">
                        <Text fontSize="sm" color="gray.500">This Month</Text>
                        <Heading size="md">${(summary.monthly /  100).toFixed(2)}</Heading>
                    </Box>
                    <Box p={4} bg={bgColor} borderRadius="md">
                        <Text fontSize="sm" color="gray.500">Pending</Text>
                        <Heading size="md">${(summary.pending / 100).toFixed(2)}</Heading>
                    </Box>
                    <Box p={4} bg={bgColor} borderRadius="md">
                        <Text fontSize="sm" color="gray.500">Bookings</Text>
                        <Heading size="md">{(summary.bookings)}</Heading>
                    </Box>
                    <Box p={4} bg={bgColor} borderRadius="md">
                        <Text fontSize="sm" color="gray.500">Signups</Text>
                        <Heading size="md">{summary.signups}</Heading>
                    </Box>
                </SimpleGrid>
            )}



            {/* Referral Box */}
            <Box mb={6} p={4} bg={useColorModeValue('gray.50', 'gray.800')} borderRadius="md">
                <Text mb={2} fontWeight="semibold">Your Referral Link</Text>
                <Flex justify="space-between" align="center">
                    <Text wordBreak="break-all">{referralLink}</Text>
                    <Button size="sm" onClick={handleCopy}>Copy</Button>
                </Flex>
            </Box>

            {/* Payout History */}
            <Box mb={6}>
                <Heading size="md" mb={2}>Payout History</Heading>
                <Table variant="simple">
                    <Thead>
                        <Tr>
                            <Th>Date</Th>
                            <Th>Amount</Th>
                            <Th>Status</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {payouts.map((row, idx) => (
                            <Tr key={idx}>
                                <Td>{new Date(row.date).toLocaleDateString()}</Td>
                                <Td>${(row.payout / 100).toFixed(2)}</Td>
                                <Td color={row.status === 'completed' ? 'green.500' : 'orange.400'}>
                                    {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Box>

            {/* Promo Vault */}
            {/* Will add script and way for user to upload clips on the platform */}
            {/* 
            <Box>
                <Heading size="md" mb={3}>Promo Vault</Heading>
                <Tabs index={tabIndex} onChange={setTabIndex}>
                    <TabList>
                        <Tab>Instagram</Tab>
                        <Tab>TikTok</Tab>
                        <Tab>Copy Text</Tab>
                    </TabList>
                    <TabPanels mt={4}>
                        <TabPanel><Text>Instagram media & captions</Text></TabPanel>
                        <TabPanel><Text>TikTok reels & scripts</Text></TabPanel>
                        <TabPanel><Text>Copy/paste promo templates</Text></TabPanel>
                    </TabPanels>
                </Tabs>
            </Box>*/}
        </Box>
    );
};

export default AffiliateDashboard;
